import axios from "axios";
import { makeAutoObservable, runInAction } from "mobx";
import type { WorkspaceAIProviderService } from "@/services/workspace-ai-provider.service";
import type {
  IAIProviderCatalogEntry,
  IConnectionTestResult,
  IWorkspaceAIProvider,
  IWorkspaceAIProviderFieldErrors,
  IWorkspaceAIProviderWrite,
} from "@/types/workspace-ai-provider";

/**
 * What may be logged about a failed request on this page — and nothing else.
 *
 * The request body of a test or save carries the provider credential in
 * plaintext, and an AxiosError keeps that body on `config.data`. Logging the
 * error object itself would put the key in the browser console and in any error
 * sink that reads console output. So every log site here logs this summary
 * instead: status, the SDK's error code and message, the URL — never the body,
 * never the headers.
 */
export function loggableError(error: unknown): {
  status: number | null;
  code: string | null;
  message: string;
  url: string | null;
} {
  if (axios.isAxiosError(error)) {
    return {
      status: error.response?.status ?? null,
      code: error.code ?? null,
      message: error.message,
      url: error.config?.url ?? null,
    };
  }
  return {
    status: null,
    code: null,
    message: error instanceof Error ? error.message : String(error),
    url: null,
  };
}

/** The slice of the root store this store reads. */
export interface WorkspaceAIProviderStoreDeps {
  workspaceAIProviderService: WorkspaceAIProviderService;
}

/** The backend refused a save because a model probe failed; `result` says which. */
export class AIProviderSaveRefusedError extends Error {
  constructor(readonly result: IConnectionTestResult) {
    super("The AI provider connection test failed; nothing was saved.");
    this.name = "AIProviderSaveRefusedError";
  }
}

/** A 400: one or more inputs were rejected before any provider call. */
export class AIProviderValidationError extends Error {
  constructor(readonly fieldErrors: IWorkspaceAIProviderFieldErrors) {
    super("The AI provider configuration has invalid fields.");
    this.name = "AIProviderValidationError";
  }
}

/** A 409: the stored credential changed while this request was being tested. */
export class AIProviderCredentialChangedError extends Error {
  constructor() {
    super("The stored credential changed while it was being tested; repeat the request.");
    this.name = "AIProviderCredentialChangedError";
  }
}

/**
 * The workspace's AI-provider setting (WS5 of NG-1357): the masked record, the
 * catalogue of providers it may choose, and the outcome of the last connection
 * test. Inputs live in the form component; this store owns what the server said.
 *
 * Save and test are the two blocking interactions on this page, deliberately not
 * optimistic: both make real provider calls whose outcome is unknowable client
 * side, and a "saved" state shown before the probe answered would mislead an admin
 * into thinking a bad key works.
 */
export class WorkspaceAIProviderStore {
  setting: IWorkspaceAIProvider | null = null;
  catalog: IAIProviderCatalogEntry[] = [];
  isLoading = false;
  loadError: unknown = null;
  isTesting = false;
  isSaving = false;
  isResetting = false;
  /** The last connection test the server ran — from the test button, or the
   *  refusal a save came back with. Cleared when the form changes provider. */
  lastTest: IConnectionTestResult | null = null;
  /** The form snapshot the last PASSING test ran against. Save is enabled only
   *  while the current form snapshot equals it — kept here beside `lastTest` so
   *  the two halves of that invariant cannot drift apart across input handlers. */
  lastTestSnapshot: string | null = null;
  fieldErrors: IWorkspaceAIProviderFieldErrors | null = null;

  private rootStore: WorkspaceAIProviderStoreDeps;

  /** Takes only what it uses (the root store satisfies this structurally), so
   *  this module does not import `root.store` and add a cycle to the graph. */
  constructor(rootStore: WorkspaceAIProviderStoreDeps) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  catalogEntry(key: string | null | undefined): IAIProviderCatalogEntry | undefined {
    return this.catalog.find((entry) => entry.key === key);
  }

  async load(slug: string): Promise<void> {
    // Everything here belongs to one workspace. Clear it before fetching the
    // next one's, so a failed load renders the error state rather than the
    // previous workspace's setting, and a passing test never carries across.
    this.setting = null;
    this.catalog = [];
    this.lastTest = null;
    this.lastTestSnapshot = null;
    this.fieldErrors = null;
    this.isLoading = true;
    this.loadError = null;
    try {
      const [setting, catalog] = await Promise.all([
        this.rootStore.workspaceAIProviderService.getSetting(slug),
        this.rootStore.workspaceAIProviderService.getCatalog(slug),
      ]);
      runInAction(() => {
        this.setting = setting;
        this.catalog = catalog;
      });
    } catch (error) {
      console.error("Failed to load the workspace AI provider setting", {
        error: loggableError(error),
        slug,
      });
      runInAction(() => {
        this.loadError = error;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** Whether the last test passed for exactly this form snapshot. */
  testPassedFor(snapshot: string): boolean {
    return Boolean(this.lastTest?.ok) && this.lastTestSnapshot === snapshot;
  }

  async testConnection(
    slug: string,
    body: IWorkspaceAIProviderWrite,
    snapshot: string,
  ): Promise<IConnectionTestResult> {
    this.isTesting = true;
    this.fieldErrors = null;
    try {
      const result = await this.rootStore.workspaceAIProviderService.testConnection(slug, body);
      runInAction(() => {
        this.lastTest = result;
        this.lastTestSnapshot = result.ok ? snapshot : null;
      });
      return result;
    } catch (error) {
      throw this.translate(error, slug, "test");
    } finally {
      runInAction(() => {
        this.isTesting = false;
      });
    }
  }

  async save(slug: string, body: IWorkspaceAIProviderWrite): Promise<IWorkspaceAIProvider> {
    this.isSaving = true;
    this.fieldErrors = null;
    try {
      const setting = await this.rootStore.workspaceAIProviderService.save(slug, body);
      runInAction(() => {
        this.setting = setting;
        this.lastTest = null;
        this.lastTestSnapshot = null;
      });
      return setting;
    } catch (error) {
      throw this.translate(error, slug, "save");
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  async reset(slug: string): Promise<void> {
    this.isResetting = true;
    try {
      await this.rootStore.workspaceAIProviderService.reset(slug);
      const setting = await this.rootStore.workspaceAIProviderService.getSetting(slug);
      runInAction(() => {
        this.setting = setting;
        this.lastTest = null;
        this.lastTestSnapshot = null;
        this.fieldErrors = null;
      });
    } catch (error) {
      console.error("Failed to reset the workspace AI provider", {
        error: loggableError(error),
        slug,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isResetting = false;
      });
    }
  }

  clearTest(): void {
    this.lastTest = null;
    this.lastTestSnapshot = null;
    this.fieldErrors = null;
  }

  /** Turn the API's three documented refusals into typed errors the form can
   *  branch on; anything else is logged and re-thrown for the generic toast. */
  private translate(error: unknown, slug: string, operation: "test" | "save"): unknown {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      if (status === 422 && operation === "save") {
        const result = data as IConnectionTestResult;
        runInAction(() => {
          this.lastTest = result;
          this.lastTestSnapshot = null;
        });
        return new AIProviderSaveRefusedError(result);
      }
      if (status === 400) {
        const fieldErrors = data as IWorkspaceAIProviderFieldErrors;
        runInAction(() => {
          this.fieldErrors = fieldErrors;
        });
        return new AIProviderValidationError(fieldErrors);
      }
      if (status === 409) {
        return new AIProviderCredentialChangedError();
      }
    }
    console.error(`Workspace AI provider ${operation} failed`, {
      error: loggableError(error),
      slug,
    });
    return error;
  }
}
